import React, { useState, useEffect, useCallback, useRef } from "react";
import "./styles/_index.scss";

interface GeographicCoordinates {
  lat: string;
  lng: string;
}

interface SectionConfig {
  id: string;
  label: string;
}

interface NavigationState {
  canMoveLeft: boolean;
  canMoveRight: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

const Satellite: React.FC = () => {
  const SECTIONS: SectionConfig[] = [
    { id: "IR", label: "Infra-Red Image" },
    { id: "V", label: "Visible Image" },
  ];

  const [section, setSection] = useState<string>(SECTIONS[0].id);
  const [imageIndex, setImageIndex] = useState<number>(1);
  const [currentImageId, setCurrentImageId] = useState<string>("");
  const [imageSrc, setImageSrc] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [errorDetails, setErrorDetails] = useState<string>("");
  const [availableImages, setAvailableImages] = useState<string[]>([]);
  const [userGeoCords, setUserGeoCords] = useState<GeographicCoordinates>({
    lat: "",
    lng: "",
  });
  const [baseImageIndex, setBaseImageIndex] = useState<number | null>(null);
  const [hasMovedFromBase, setHasMovedFromBase] = useState<boolean>(false);
  const [moveDirection, setMoveDirection] = useState<string | null>(null);
  const [navigationState, setNavigationState] = useState<NavigationState>({
    canMoveLeft: true,
    canMoveRight: true,
    canMoveUp: true,
    canMoveDown: true,
  });
  const isInitialLoadRef = useRef(true);
  const currentSectionRef = useRef(section);
  const availableImagesRef = useRef(availableImages);
  const currentImageIdRef = useRef(currentImageId);
  const [navigationStep, setNavigationStep] = useState(0);
  const [actualFilename, setActualFilename] = useState<string>("");

  const IMAGES_PER_SET = 8;

  const customRound = (value: number): number => {
    return Math.round(value);
  };

  const applyBaseConstraints = useCallback((direction: string) => {
    // console.log(`Applying ${direction} constraints from base image ${baseImageIndex}`);
    
    if (direction === 'right') {
      setNavigationState({
        canMoveLeft: true,
        canMoveRight: false,
        canMoveUp: false,
        canMoveDown: false,
      });
    } else if (direction === 'left') {
      setNavigationState({
        canMoveLeft: false,
        canMoveRight: true,
        canMoveUp: false,
        canMoveDown: false,
      });
    } else if (direction === 'up') {
      setNavigationState({
        canMoveLeft: false,
        canMoveRight: false,
        canMoveUp: false,
        canMoveDown: true,
      });
    } else if (direction === 'down') {
      setNavigationState({
        canMoveLeft: false,
        canMoveRight: false,
        canMoveUp: true,
        canMoveDown: false,
      });
    }
    
    setHasMovedFromBase(true);
    setMoveDirection(direction);
  }, [baseImageIndex]);

  const applyDefaultConstraints = useCallback((newIndex: number, totalImages: number) => {
    const canMoveLeft = newIndex - 8 >= 1;
    const canMoveRight = newIndex + 8 <= totalImages;
    const canMoveUp = newIndex + 1 <= totalImages;
    const canMoveDown = newIndex - 1 >= 1;
    
    setNavigationState({
      canMoveLeft,
      canMoveRight,
      canMoveUp,
      canMoveDown,
    });
  }, []);

  const getNearestLatLon = (lat: number, lon: number) => {
    const nearestLat = Math.round(Math.abs(lat) / 5) * 5;
    const nearestLon = Math.round(lon / 5) * 5;
    const diffLat = Math.abs(lat) - nearestLat;
    const diffLon = lon - nearestLon;
    const claty =
      lat > 0
        ? customRound((5 - diffLat) * 55.6 + 22)
        : customRound((5 + diffLat) * 55.6 + 22);
    const clatx = customRound((5 + diffLon) * 55.6 + 22);
    return { nearestLat, nearestLon, clatx, claty };
  };

  const extractImageIdFromUrl = (url: string): string | null => {
    try {
      if (url.includes("satpic-")) {
        const match = url.match(/satpic-[\w-]*\d{4}/);
        return match ? match[0] : null;
      }
      return null;
    } catch (e) {
      console.error("Error extracting image ID from URL:", e);
      return null;
    }
  };

  const fetchAvailableImages = useCallback(async (currentSection: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_IP || ""}/api/api/available_images/${currentSection}/`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch images for ${currentSection}`);
      }
      const imagesData = await response.json();
      const images = imagesData.images || [];
      setAvailableImages(images);
      availableImagesRef.current = images;
      return images;
    } catch (error) {
      console.error(`Error fetching available images for ${currentSection}:`, error);
      setErrorDetails(
        `Failed to load images: ${error instanceof Error ? error.message : "Unknown error"}`
      );
      setAvailableImages([]);
      availableImagesRef.current = [];
      return [];
    }
  }, []);

  const extractDisplayName = (filename: string, section: string): string => {
    const parts = filename.replace(".png", "").split("-");
    const sliceIndex = section === "V" ? 3 : 2;
    return parts.length > sliceIndex
      ? parts.slice(sliceIndex).join(" ")
      : filename.replace(".png", "");
  };

  const fetchImageByCoordinates = useCallback(
    async (currentSection: string, coords: GeographicCoordinates) => {
      setIsLoading(true);
      setLoadError(false);
      setErrorDetails("");
      try {
        if (!coords.lat || !coords.lng) {
          throw new Error("Missing coordinates for image fetch");
        }
        const lat = parseFloat(coords.lat);
        const lon = parseFloat(coords.lng);
        const { nearestLat, nearestLon, clatx, claty } = getNearestLatLon(lat, lon);
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_IP || ""}/api/api/display_image/${currentSection}/static-0001/?lat=${coords.lat}&lon=${coords.lng}&mode=api`
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch image by coordinates: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        if (data && data.image_url) {
          setImageSrc(data.image_url);
          setActualFilename(extractDisplayName(data.actual_filename, currentSection));
          const imageNameFromUrl =
            data.matched_image_id ||
            extractImageIdFromUrl(data.image_url) ||
            availableImagesRef.current[0];
          if (!imageNameFromUrl) {
            throw new Error("Could not determine image ID");
          }
          setCurrentImageId(imageNameFromUrl);
          currentImageIdRef.current = imageNameFromUrl;
          const foundIndex = availableImagesRef.current.findIndex((img) => img === imageNameFromUrl);
          if (foundIndex >= 0) {
            const newIndex = foundIndex + 1;
            setImageIndex(newIndex);
            setBaseImageIndex(newIndex);
            setHasMovedFromBase(false);
            setMoveDirection(null);
            // console.log(`BASE IMAGE SET TO: ${newIndex}`);
            applyDefaultConstraints(newIndex, availableImagesRef.current.length);
          } else {
            const newIndex = 1;
            setImageIndex(newIndex);
            setBaseImageIndex(newIndex);
            setHasMovedFromBase(false);
            setMoveDirection(null);
            // console.log(`BASE IMAGE SET TO: ${newIndex}`);
            applyDefaultConstraints(newIndex, availableImagesRef.current.length);
          }
        } else {
          throw new Error("Invalid image data received");
        }
      } catch (error) {
        console.error("Error fetching image by coordinates:", error);
        setImageSrc("");
        setLoadError(true);
        setErrorDetails(error instanceof Error ? error.message : "Unknown error");
      } finally {
        setIsLoading(false);
      }
    },
    [applyDefaultConstraints]
  );

  const fetchImageById = useCallback(
    async (currentSection: string, imageId: string) => {
      setIsLoading(true);
      setLoadError(false);
      setErrorDetails("");
      try {
        if (!userGeoCords.lat || !userGeoCords.lng) {
          throw new Error("Missing coordinates from backend");
        }
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_IP || ""}/api/api/display_image/${currentSection}/${imageId}/?lat=${userGeoCords.lat}&lon=${userGeoCords.lng}&mode=api`
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        if (data && data.image_url) {
          setImageSrc(data.image_url);
          setActualFilename(extractDisplayName(data.actual_filename, currentSection));
          setCurrentImageId(imageId);
          currentImageIdRef.current = imageId;
        } else {
          throw new Error("Invalid image data received");
        }
      } catch (error) {
        console.error("Error fetching image by ID:", error);
        setImageSrc("");
        setLoadError(true);
        setErrorDetails(error instanceof Error ? error.message : "Unknown error");
      } finally {
        setIsLoading(false);
      }
    },
    [userGeoCords]
  );

  const fetchInitialData = useCallback(async () => {
    try {
      const fid = localStorage.getItem("fid") || "default";
      const locationResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_IP || ""}/api/cyclone/${fid}/`
      );
      if (!locationResponse.ok) {
        throw new Error(`Failed to fetch project location: ${locationResponse.status}`);
      }
      const locationData = await locationResponse.json();
      const { latitude, longitude } = locationData.project_location || {};
      if (latitude && longitude) {
        const coords = {
          lat: latitude.toString(),
          lng: longitude.toString(),
        };
        setUserGeoCords(coords);
        await fetchAvailableImages(section);
        await fetchImageByCoordinates(section, coords);
      } else {
        throw new Error("No coordinates received from backend");
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
      setErrorDetails(`Error initializing: ${error instanceof Error ? error.message : "Unknown error"}`);
      setIsLoading(false);
      setLoadError(true);
    }
  }, [section, fetchAvailableImages, fetchImageByCoordinates]);

  const handleSectionChange = useCallback(
    async (newSection: string) => {
      if (section !== newSection) {
        setSection(newSection);
        currentSectionRef.current = newSection;
        
        const newImages = await fetchAvailableImages(newSection);
        const currentIndexToMaintain = imageIndex;

        if (newImages.length > 0) {
          const targetIndex = Math.min(currentIndexToMaintain, newImages.length);
          const newImageId = newImages[targetIndex - 1];

          setImageIndex(targetIndex);
          await fetchImageById(newSection, newImageId);

          if (targetIndex === baseImageIndex && !hasMovedFromBase) {
            applyDefaultConstraints(targetIndex, newImages.length);
          } else if (targetIndex === baseImageIndex && hasMovedFromBase) {
            setHasMovedFromBase(false);
            setMoveDirection(null);
            applyDefaultConstraints(targetIndex, newImages.length);
          } else if (hasMovedFromBase && moveDirection) {
            applyBaseConstraints(moveDirection);
          } else {
            applyDefaultConstraints(targetIndex, newImages.length);
          }
          
          // console.log(`Switched to section ${newSection} at index ${targetIndex}, base: ${baseImageIndex}, moved: ${hasMovedFromBase}, direction: ${moveDirection}`);
        } else {
          await fetchImageByCoordinates(newSection, userGeoCords);
        }
      }
    },
    [
      section,
      imageIndex,
      fetchAvailableImages,
      fetchImageById,
      fetchImageByCoordinates,
      userGeoCords,
      applyDefaultConstraints,
      applyBaseConstraints,
      baseImageIndex,
      hasMovedFromBase,
      moveDirection,
    ]
  );

  // Navigation handlers
  const handleNextImage = useCallback(() => {
    const currentImages = availableImagesRef.current;
    const currentSection = currentSectionRef.current;
    if (currentImages.length === 0) return;
    
    const proposedIndex = imageIndex + 8;
    if (proposedIndex > currentImages.length) return;
    
    const isMovingFromBase = !hasMovedFromBase && imageIndex === baseImageIndex;
    
    setImageIndex(proposedIndex);
    const newImageId = currentImages[proposedIndex - 1];
    fetchImageById(currentSection, newImageId);
    
    if (isMovingFromBase) {
      // console.log(`FIRST MOVE FROM BASE ${baseImageIndex}: RIGHT → ${proposedIndex}`);
      applyBaseConstraints('right');
    } else {
      if (proposedIndex === baseImageIndex) {
        // console.log(`RETURNED TO BASE IMAGE ${baseImageIndex} - resetting`);
        setHasMovedFromBase(false);
        setMoveDirection(null);
        applyDefaultConstraints(proposedIndex, currentImages.length);
      } else {
        applyDefaultConstraints(proposedIndex, currentImages.length);
      }
    }
  }, [imageIndex, fetchImageById, hasMovedFromBase, baseImageIndex, applyBaseConstraints, applyDefaultConstraints]);

  const handlePrevImage = useCallback(() => {
    const currentImages = availableImagesRef.current;
    const currentSection = currentSectionRef.current;
    if (currentImages.length === 0) return;
    
    const proposedIndex = imageIndex - 8;
    if (proposedIndex < 1) return;
    
    const isMovingFromBase = !hasMovedFromBase && imageIndex === baseImageIndex;
    
    setImageIndex(proposedIndex);
    const newImageId = currentImages[proposedIndex - 1];
    fetchImageById(currentSection, newImageId);
    
    if (isMovingFromBase) {
      // console.log(`FIRST MOVE FROM BASE ${baseImageIndex}: LEFT → ${proposedIndex}`);
      applyBaseConstraints('left');
    } else {
      if (proposedIndex === baseImageIndex) {
        // console.log(`RETURNED TO BASE IMAGE ${baseImageIndex} - resetting`);
        setHasMovedFromBase(false);
        setMoveDirection(null);
        applyDefaultConstraints(proposedIndex, currentImages.length);
      } else {
        applyDefaultConstraints(proposedIndex, currentImages.length);
      }
    }
  }, [imageIndex, fetchImageById, hasMovedFromBase, baseImageIndex, applyBaseConstraints, applyDefaultConstraints]);

  const handlePrevSet = useCallback(() => {
    const currentImages = availableImagesRef.current;
    const currentSection = currentSectionRef.current;
    if (currentImages.length === 0) return;
    
    const newIndex = imageIndex + 1;
    if (newIndex > currentImages.length) return;
    
    const isMovingFromBase = !hasMovedFromBase && imageIndex === baseImageIndex;
    
    setImageIndex(newIndex);
    const newImageId = currentImages[newIndex - 1];
    fetchImageById(currentSection, newImageId);
    
    if (isMovingFromBase) {
      // console.log(`FIRST MOVE FROM BASE ${baseImageIndex}: DOWN → ${newIndex}`);
      applyBaseConstraints('down');
    } else {
      if (newIndex === baseImageIndex) {
        // console.log(`RETURNED TO BASE IMAGE ${baseImageIndex} - resetting`);
        setHasMovedFromBase(false);
        setMoveDirection(null);
        applyDefaultConstraints(newIndex, currentImages.length);
      } else {
        applyDefaultConstraints(newIndex, currentImages.length);
      }
    }
  }, [imageIndex, fetchImageById, hasMovedFromBase, baseImageIndex, applyBaseConstraints, applyDefaultConstraints]);

  const handleNextSet = useCallback(() => {
    const currentImages = availableImagesRef.current;
    const currentSection = currentSectionRef.current;
    if (currentImages.length === 0) return;
    
    const newIndex = imageIndex - 1;
    if (newIndex < 1) return;
    
    const isMovingFromBase = !hasMovedFromBase && imageIndex === baseImageIndex;
    
    setImageIndex(newIndex);
    const newImageId = currentImages[newIndex - 1];
    fetchImageById(currentSection, newImageId);
    
    if (isMovingFromBase) {
      // console.log(`FIRST MOVE FROM BASE ${baseImageIndex}: UP → ${newIndex}`);
      applyBaseConstraints('up');
    } else {
      if (newIndex === baseImageIndex) {
        // console.log(`RETURNED TO BASE IMAGE ${baseImageIndex} - resetting`);
        setHasMovedFromBase(false);
        setMoveDirection(null);
        applyDefaultConstraints(newIndex, currentImages.length);
      } else {
        applyDefaultConstraints(newIndex, currentImages.length);
      }
    }
  }, [imageIndex, fetchImageById, hasMovedFromBase, baseImageIndex, applyBaseConstraints, applyDefaultConstraints]);

  useEffect(() => {
    setNavigationStep(0);
  }, [section]);

  useEffect(() => {
    if (isInitialLoadRef.current) {
      fetchInitialData();
      isInitialLoadRef.current = false;
    }
  }, [fetchInitialData]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        handleNextImage();
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        handlePrevImage();
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        handlePrevSet();
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        handleNextSet();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleNextImage, handlePrevImage, handleNextSet, handlePrevSet]);

  return (
    <div className="outer-container">
      <div className="inner-container">
        <div className="image-frame">
          <button
            style={{
              position: "absolute",
              top: "-5px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "40px",
              height: "40px",
              backgroundColor: navigationState.canMoveUp ? "#003366" : "#666666",
              color: "#ffffff",
              cursor: navigationState.canMoveUp ? "pointer" : "not-allowed",
              fontSize: "18",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
              padding: 0,
              opacity: navigationState.canMoveUp ? 1 : 0.5,
            }}
            className="nav-btn nav-btn-top"
            onClick={handleNextSet}
            aria-label="Next Set"
            disabled={isLoading || !navigationState.canMoveUp}
          >
            ⬆
          </button>
          <button
            style={{
              position: "absolute",
              left: "-5px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "40px",
              height: "40px",
              backgroundColor: navigationState.canMoveLeft ? "#003366" : "#666666",
              color: "#ffffff",
              cursor: navigationState.canMoveLeft ? "pointer" : "not-allowed",
              fontSize: "18",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
              padding: 0,
              opacity: navigationState.canMoveLeft ? 1 : 0.5,
            }}
            className="nav-btn nav-btn-prev"
            onClick={handlePrevImage}
            aria-label="Previous Image"
            disabled={isLoading || !navigationState.canMoveLeft}
          >
            ⬅
          </button>
          {isLoading ? (
            <div className="loading-indicator">Loading satellite image...</div>
          ) : loadError ? (
            <div className="error-message">
              Failed to load image. {errorDetails}
            </div>
          ) : (
            imageSrc && (
              <img
                src={imageSrc}
                alt={`Satellite imagery (${section} section)`}
                className="center-image"
                onError={() => setLoadError(true)}
              />
            )
          )}
          <button
            style={{
              position: "absolute",
              right: "-5px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "40px",
              height: "40px",
              backgroundColor: navigationState.canMoveRight ? "#003366" : "#666666",
              color: "#ffffff",
              cursor: navigationState.canMoveRight ? "pointer" : "not-allowed",
              fontSize: "18",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
              padding: 0,
              opacity: navigationState.canMoveRight ? 1 : 0.5,
            }}
            className="nav-btn nav-btn-next"
            onClick={handleNextImage}
            aria-label="Next Image"
            disabled={isLoading || !navigationState.canMoveRight}
          >
            ⮕
          </button>
          <button
            style={{
              position: "absolute",
              bottom: "-5px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "40px",
              height: "40px",
              backgroundColor: navigationState.canMoveDown ? "#003366" : "#666666",
              color: "#ffffff",
              cursor: navigationState.canMoveDown ? "pointer" : "not-allowed",
              fontSize: "18",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 20,
              padding: 0,
              opacity: navigationState.canMoveDown ? 1 : 0.5,
            }}
            className="nav-btn nav-btn-bottom"
            onClick={handlePrevSet}
            aria-label="Previous Set"
            disabled={isLoading || !navigationState.canMoveDown}
          >
            ⬇
          </button>
          <div className="section-buttons">
            {SECTIONS.map((sectionConfig) => (
              <button
                key={sectionConfig.id}
                className={`section-btn ${section === sectionConfig.id ? "active" : ""}`}
                onClick={() => handleSectionChange(sectionConfig.id)}
              >
                {sectionConfig.label}
              </button>
            ))}
          </div>
          {!isLoading && !loadError && imageSrc && (
            <div className="image-name-display">
              {actualFilename || currentImageId || "Satellite Image"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Satellite;